/**
 * Permission Utilities
 * Role-based permission checks for various application features
 */
import type { User, UserRole, Assessment, AssessmentStatus, DraftEvidencePermissions } from '@app-types';
import { ROLES, ROLE_GROUPS, ACTIVE_PUBLISHED_STATUSES, CANCELLABLE_STATUSES } from '@app-types';

/**
 * Helper function to check if user has a specific role
 * @param user - The user object
 * @param role - Single role or array of roles to check
 * @returns true if user has at least one of the specified roles
 */
export const hasRole = (user: User | null | undefined, role: UserRole | UserRole[]): boolean => {
  if (!user?.roles) return false;
  const rolesToCheck = Array.isArray(role) ? role : [role];
  return user.roles.some((r) => rolesToCheck.includes(r));
};

/**
 * Check if user can edit an assessment
 */
export const canUserEditAssessment = (user: User, assessment: Assessment): boolean => {
  const terminalStatuses: AssessmentStatus[] = ['Final', 'Cancelled', 'Expired'];
  if (terminalStatuses.includes(assessment.assessment_status)) return false;

  // Admin/Program Lead can edit any assessment
  if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;

  if (hasRole(user, ROLES.ASSESSOR)) {
    return assessment.assessor_id === user.id;
  }

  return false;
};

/**
 * Check if user can reassign an assessment to a different assessor
 */
export const canUserReassignAssessment = (user: User, assessment?: Assessment): boolean => {
  const terminalStatuses: AssessmentStatus[] = ['Final', 'Cancelled', 'Expired'];
  if (assessment && terminalStatuses.includes(assessment.assessment_status)) return false;

  return hasRole(user, ROLE_GROUPS.FULL_ACCESS);
};

/**
 * Check if user can evaluate assessment items
 */
export const canUserEvaluateItems = (user: User, assessment: Assessment): boolean => {
  const blockedStatuses: AssessmentStatus[] = [
    'Pending Evidence',
    'Evidence Past Due',
    'Escalated',
    'Final',
    'Cancelled',
    'Expired'
  ];
  if (blockedStatuses.includes(assessment.assessment_status)) return false;

  // Admin/Program Lead can always evaluate
  if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return true;

  // Only assigned assessor can evaluate items
  if (hasRole(user, ROLES.ASSESSOR)) {
    return assessment.assessor_id === user.id;
  }

  return false;
};

/**
 * Check if user can view an assessment
 */
export const canUserViewAssessment = (user: User, assessment: Assessment): boolean => {
  // All roles can view assessments (with potential filtering)
  // Add specific logic here if needed based on business rules
  return true;
};

/**
 * Check if the Assessment Framework section is shown on Assessment details.
 * Hidden for Admin (high-level management); Program Lead and multi-role users
 * (e.g. Assessor + Program Lead) see it (ADO-1195, ADO-1200).
 */
export const canUserViewAssessmentFramework = (user: User | null | undefined): boolean => {
  return !hasRole(user, ROLES.ADMIN);
};

/**
 * Check if user can generate assessment items (framework)
 */
export const canUserGenerateAssessmentItems = (user: User, assessment?: Assessment): boolean => {
  const blockedStatuses: AssessmentStatus[] = [
    'Pending Evidence',
    'Evidence Past Due',
    'Escalated',
    'Final',
    'Cancelled',
    'Expired'
  ];
  if (assessment && blockedStatuses.includes(assessment.assessment_status)) return false;

  return hasRole(user, ROLE_GROUPS.ASSESSMENT_EDITORS);
};

/**
 * Check if user can access assessments
 */
export const canUserAccessAssessments = (user: User | null | undefined): boolean => {
  return hasRole(user, ROLE_GROUPS.ASSESSMENT_VIEWERS);
};

export type CancelAssessmentAccess = 'hidden' | 'blocked' | 'enabled';

/**
 * Whether the user can cancel an assessment in the given status. Mirrors the
 * backend rule: only a Program Lead or Admin can cancel a published (Active
 * group) assessment; an Assessor sees the action blocked there. A user who is
 * both Assessor and Program Lead/Admin gets Program Lead/Admin rights.
 */
export const canUserCancelAssessment = (
  user: User | null | undefined,
  status: AssessmentStatus
): CancelAssessmentAccess => {
  if (!CANCELLABLE_STATUSES.includes(status)) return 'hidden';
  if (hasRole(user, ROLE_GROUPS.FULL_ACCESS)) return 'enabled';
  if (!hasRole(user, ROLES.ASSESSOR)) return 'hidden';
  return ACTIVE_PUBLISHED_STATUSES.includes(status) ? 'blocked' : 'enabled';
};

/**
 * Who may upload evidence for an assessment item: Contract Role users (the
 * backend narrows this to the contract's CM/CO) and the assigned Assessor,
 * whose upload becomes Draft Evidence (ADO-1148) and is allowed only once the
 * assessment is published. Mirrors the backend upload rule.
 */
export const canUserUploadEvidence = (
  user: User | null | undefined,
  assessorId: number | null | undefined,
  assessmentStatus: AssessmentStatus | undefined
): boolean => {
  if (hasRole(user, ROLES.CONTRACT_ROLE)) return true;
  return (
    hasRole(user, ROLES.ASSESSOR) &&
    assessorId != null &&
    user?.id === assessorId &&
    assessmentStatus !== undefined &&
    ACTIVE_PUBLISHED_STATUSES.includes(assessmentStatus)
  );
};

/**
 * Actions on Draft Evidence. Mirrors the backend: only the contract's CM/CO
 * submits; the CM/CO and the Assessor who uploaded the draft edit and delete;
 * Admin and Program Lead may only delete a stuck draft. The backend narrows
 * "Contract Role" to the contract's CM/CO. `draftUploadedById` is undefined
 * until the draft record is loaded, so the uploader's rights appear then.
 */
export const getDraftEvidencePermissions = (
  user: User | null | undefined,
  draftUploadedById: number | undefined
): DraftEvidencePermissions => {
  const isContractRole = hasRole(user, ROLES.CONTRACT_ROLE);
  const isUploadingAssessor =
    hasRole(user, ROLES.ASSESSOR) && draftUploadedById !== undefined && user?.id === draftUploadedById;
  const canEdit = isContractRole || isUploadingAssessor;
  return {
    canSubmit: isContractRole,
    canEdit,
    canDelete: canEdit || hasRole(user, ROLE_GROUPS.FULL_ACCESS)
  };
};
